import React, { useEffect, useRef, useState } from "react";
import { http, formatError } from "@/lib/api";
import { mediaSrc } from "@/lib/media";
import { toast } from "sonner";
import { Upload, Trash2, Plus, X, Lock, Unlock, Link as LinkIcon } from "lucide-react";

const empty = {
  type: "photo",
  data_base64: "",
  storage_path: "",
  external_url: "",
  caption: "",
  is_premium: false,
  one_time_price: 0,
  order: 0,
};

export default function AdminMedia() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("photo");
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef();

  const load = () => http.get("/media").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const resetForm = () => {
    setForm(empty);
    setPreviewUrl("");
    setUploadPct(0);
  };

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const isVideo = f.type.startsWith("video");
    const maxMB = isVideo ? 200 : 20;
    if (f.size / 1024 / 1024 > maxMB) {
      toast.error(`Max ${maxMB}MB for ${isVideo ? "videos" : "photos"}. For larger videos, paste a YouTube/Vimeo URL.`);
      return;
    }
    setUploading(true);
    setUploadPct(0);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const { data } = await http.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setForm((p) => ({
        ...p,
        storage_path: data.storage_path,
        data_base64: "",
        type: data.type,
      }));
      setPreviewUrl(URL.createObjectURL(f));
      toast.success("Upload complete");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setUploading(false);
    }
  };

  const create = async () => {
    if (!form.storage_path && !form.data_base64 && !form.external_url) {
      toast.error("Upload a file or paste a video URL.");
      return;
    }
    try {
      const payload = {
        ...form,
        one_time_price: form.is_premium && form.one_time_price ? parseFloat(form.one_time_price) : null,
      };
      await http.post("/media", payload);
      toast.success("Saved to gallery");
      setOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete?")) return;
    await http.delete(`/media/${id}`);
    load();
  };

  const togglePremium = async (item) => {
    try {
      await http.put(`/media/${item.id}`, {
        type: item.type,
        data_base64: item.data_base64,
        storage_path: item.storage_path,
        external_url: item.external_url,
        thumbnail_base64: item.thumbnail_base64,
        thumbnail_storage_path: item.thumbnail_storage_path,
        caption: item.caption,
        is_premium: !item.is_premium,
        required_tier: item.required_tier,
        one_time_price: item.one_time_price,
        order: item.order,
      });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const filtered = items.filter((i) => i.type === tab);

  return (
    <div data-testid="admin-media-page">
      <div className="flex justify-between items-end mb-6 flex-wrap gap-4">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-pink-blush mb-2">Gallery</div>
          <h1 className="font-display text-4xl text-champagne">Photos & <span className="italic text-pink-hot">videos</span></h1>
        </div>
        <button onClick={() => setOpen(true)} className="px-5 py-3 bg-pink-hot text-white text-xs tracking-[0.28em] uppercase rounded-sm hover:bg-pink-glow flex items-center gap-2" data-testid="add-media-btn">
          <Plus className="w-4 h-4" /> Upload
        </button>
      </div>

      <div className="flex gap-2 mb-5">
        {["photo", "video"].map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-4 py-2 text-[11px] tracking-[0.28em] uppercase rounded-sm ${
              tab === t ? "bg-pink-hot text-white glow-pink" : "border border-rosegold/20 text-champagne/70 hover:border-pink-hot/40"
            }`}
            data-testid={`admin-media-tab-${t}`}
          >
            {t}s
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((m) => (
          <div key={m.id} className="glass-panel rounded-md overflow-hidden group relative" data-testid={`admin-media-card-${m.id}`}>
            <div className="aspect-video bg-noir-base relative">
              {m.type === "photo" ? (
                <img src={mediaSrc(m)} alt={m.caption} className="w-full h-full object-cover" />
              ) : m.external_url ? (
                <div className="flex items-center justify-center h-full text-xs text-champagne/70 p-4 text-center">
                  <div>
                    <LinkIcon className="w-5 h-5 mx-auto mb-2 text-pink-hot" />
                    {m.external_url.slice(0, 50)}
                  </div>
                </div>
              ) : (
                <video src={mediaSrc(m)} className="w-full h-full object-cover" muted />
              )}
              {m.is_premium && (
                <div className="absolute top-2 left-2 px-2 py-1 bg-pink-hot/90 text-white text-[9px] tracking-[0.25em] uppercase rounded-sm flex items-center gap-1">
                  <Lock className="w-3 h-3" /> Premium
                </div>
              )}
            </div>
            <div className="p-4 space-y-2">
              <div className="text-sm text-champagne line-clamp-2">{m.caption || <span className="italic text-champagne/40">No caption</span>}</div>
              {m.one_time_price ? <div className="text-xs text-pink-blush">${m.one_time_price.toFixed(2)} unlock</div> : null}
              <div className="flex justify-between items-center pt-2">
                <button onClick={() => togglePremium(m)} className="text-xs text-rosegold hover:text-pink-hot flex items-center gap-1" data-testid={`toggle-premium-${m.id}`}>
                  {m.is_premium ? <Unlock className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
                  {m.is_premium ? "Make Public" : "Make Premium"}
                </button>
                <button onClick={() => remove(m.id)} className="text-champagne/60 hover:text-red-400" data-testid={`delete-media-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-noir-base/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setOpen(false); resetForm(); }}>
          <div className="glass-panel rounded-md w-full max-w-lg p-6 space-y-4" onClick={(e) => e.stopPropagation()} data-testid="media-upload-modal">
            <div className="flex justify-between items-center">
              <div className="font-display text-2xl text-champagne">New upload</div>
              <button onClick={() => { setOpen(false); resetForm(); }} className="text-champagne/60 hover:text-pink-hot"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex gap-2">
              {["photo", "video"].map((t) => (
                <button
                  key={t}
                  onClick={() => setForm({ ...form, type: t })}
                  className={`px-4 py-2 text-[10px] tracking-[0.28em] uppercase rounded-sm ${
                    form.type === t ? "bg-pink-hot text-white" : "border border-rosegold/20 text-champagne/70"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            <div
              onClick={() => !uploading && fileRef.current?.click()}
              className={`border-2 border-dashed rounded-md p-8 text-center cursor-pointer transition-colors ${
                uploading ? "border-pink-hot/60 cursor-wait" : "border-pink-hot/30 hover:border-pink-hot/60"
              }`}
              data-testid="upload-dropzone"
            >
              {previewUrl && form.type === "photo" ? (
                <img src={previewUrl} alt="preview" className="max-h-48 mx-auto mb-3 rounded-sm" />
              ) : previewUrl && form.type === "video" ? (
                <video src={previewUrl} className="max-h-48 mx-auto mb-3 rounded-sm" muted />
              ) : (
                <Upload className="w-8 h-8 text-pink-hot mx-auto mb-3" />
              )}
              <div className="text-sm text-champagne">
                {uploading
                  ? `Uploading… ${uploadPct}%`
                  : form.storage_path
                    ? "Uploaded ✓ (click to replace)"
                    : `Click to upload (max ${form.type === "video" ? "200MB" : "20MB"})`}
              </div>
              <div className="text-xs text-champagne/60 mt-1">{form.type === "video" ? "MP4/WebM/MOV accepted" : "JPG/PNG/WebP accepted"}</div>
              {uploading && (
                <div className="mt-3 h-1.5 bg-noir-base rounded-full overflow-hidden">
                  <div className="h-full bg-pink-hot transition-all" style={{ width: `${uploadPct}%` }} />
                </div>
              )}
              <input
                ref={fileRef}
                type="file"
                accept={form.type === "video" ? "video/*" : "image/*"}
                onChange={handleFile}
                className="hidden"
                data-testid="media-file-input"
              />
            </div>

            {form.type === "video" && (
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-pink-blush mb-2">...or paste a video URL</div>
                <input
                  placeholder="YouTube / Vimeo / direct MP4 URL"
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  className="w-full bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne"
                  data-testid="media-url"
                />
              </div>
            )}

            <input
              placeholder="Caption (optional)"
              value={form.caption}
              onChange={(e) => setForm({ ...form, caption: e.target.value })}
              className="w-full bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne"
              data-testid="media-caption"
            />

            <label className="flex items-center gap-2 text-sm text-champagne">
              <input type="checkbox" checked={form.is_premium} onChange={(e) => setForm({ ...form, is_premium: e.target.checked })} data-testid="media-premium-toggle" />
              Mark as Premium (subscriber only)
            </label>

            {form.is_premium && (
              <div>
                <div className="text-[10px] tracking-[0.28em] uppercase text-pink-blush mb-2">One-time unlock price (optional)</div>
                <input
                  type="number"
                  placeholder="e.g. 9.99"
                  value={form.one_time_price || ""}
                  onChange={(e) => setForm({ ...form, one_time_price: e.target.value })}
                  className="w-full bg-noir-base border border-rosegold/20 px-4 py-3 text-sm rounded-sm focus:border-pink-hot focus:outline-none text-champagne"
                  data-testid="media-price"
                />
              </div>
            )}

            <button onClick={create} disabled={uploading} className="w-full py-3 bg-pink-hot text-white text-xs tracking-[0.3em] uppercase rounded-sm hover:bg-pink-glow glow-pink transition-all disabled:opacity-50 disabled:cursor-not-allowed" data-testid="media-save">
              {uploading ? "Uploading…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
